import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Typography, Paper, Stack, Chip, Tooltip, IconButton, Menu, MenuItem, InputBase, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Select, CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CSPMResourcesDrawer from './CSPMResourcesDrawer';

const DEFAULT_COLUMNS = [
  { key: "Title", label: "Title", minWidth: 220, maxWidth: 600 },
  { key: "Severity", label: "Severity", minWidth: 100 },
  { key: "Status", label: "Status", minWidth: 100 },
  { key: "NumResourcesScanned", label: "Resources", minWidth: 200 },
];

function getSeverityBadgeProps(sev) {
  const s = (sev || '').toUpperCase();
  let color = '#e0e0e0', text = '#333';
  if (s === 'CRITICAL') { color = '#ffebee'; text = '#b71c1c'; }
  else if (s === 'HIGH') { color = '#ffebee'; text = '#f44336'; }
  else if (s === 'MEDIUM') { color = '#fff9c4'; text = '#fbc02d'; }
  else if (s === 'LOW') { color = '#e8f5e9'; text = '#4caf50'; }
  else if (s === 'INFORMATIONAL') { color = '#e3f2fd'; text = '#1976d2'; }
  return { color, text };
}

function getStatusBadgeProps(status) {
  const s = (status || '').toUpperCase();
  let color = '#e0e0e0', text = '#333';
  if (s === 'FAILED') { color = '#ffebee'; text = '#d32f2f'; }
  else if (s === 'PASSED') { color = '#e8f5e9'; text = '#388e3c'; }
  else if (s === 'MANUAL') { color = '#e3f2fd'; text = '#1976d2'; }
  else if (s === 'SKIPPED') { color = '#f3fafd'; text = '#0288d1'; }
  return { color, text };
}

const CSPMGrid = ({ scanObj }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [search, setSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCheckId, setSelectedCheckId] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [drawerAvailableRegions, setDrawerAvailableRegions] = useState([]);
  const [drawerAvailableTypes, setDrawerAvailableTypes] = useState([]);
  const [drawerCheckTitle, setDrawerCheckTitle] = useState("");
  const availableStatuses = useMemo(() => {
    // Collect unique statuses from checks
    const arr = scanObj?.auditChecks?.Checks || [];
    return Array.from(new Set(arr.map(c => c.Status))).filter(Boolean);
  }, [scanObj]);
  const availableSeverities = useMemo(() => {
    const arr = scanObj?.auditChecks?.Checks || [];
    return Array.from(new Set(arr.map(c => (c.Severity || '').toUpperCase()))).filter(Boolean);
  }, [scanObj]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedService, setSelectedService] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Table data
  const severityOrder = {
    'CRITICAL': 1,
    'HIGH': 2,
    'MEDIUM': 3,
    'LOW': 4,
    'INFORMATIONAL': 5
  };
  const checks = useMemo(() => {
    const arr = scanObj?.auditChecks?.Checks ? [...scanObj.auditChecks.Checks] : [];
    return arr.sort((a, b) => {
      // Sort by Status: FAILED first
      if ((a.Status === 'FAILED') && (b.Status !== 'FAILED')) return -1;
      if ((a.Status !== 'FAILED') && (b.Status === 'FAILED')) return 1;
      // Then by Severity order
      const sevA = severityOrder[(a.Severity || '').toUpperCase()] || 99;
      const sevB = severityOrder[(b.Severity || '').toUpperCase()] || 99;
      return sevA - sevB;
    });
  }, [scanObj]);

  // Sorting logic
  const sortedChecks = useMemo(() => {
    let arr = [...checks];
    if (sortConfig.key) {
      arr.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (sortConfig.key === 'NumResourcesScanned') {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        } else {
          aVal = (aVal || '').toString().toUpperCase();
          bVal = (bVal || '').toString().toUpperCase();
        }
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return arr;
  }, [checks, sortConfig]);

  // Compute unique list of all AvailableTypes (services)
  const availableServices = useMemo(() => {
    if (!scanObj?.auditChecks?.Checks) return [];
    const allTypes = scanObj.auditChecks.Checks.flatMap(check => check.AvailableTypes || []);
    return Array.from(new Set(allTypes)).sort();
  }, [scanObj]);

  // Filtering logic
  useEffect(() => {
    let rows = sortedChecks;
    if (selectedStatus !== 'all') {
      rows = rows.filter(check => check.Status === selectedStatus);
    }
    if (selectedSeverity !== 'all') {
      rows = rows.filter(check => (check.Severity || '').toUpperCase() === selectedSeverity);
    }
    if (selectedService !== 'all') {
      rows = rows.filter(check => (check.AvailableTypes || []).includes(selectedService));
    }
    if (activeFilters.length > 0) {
      rows = rows.filter(check => activeFilters.includes(check.Status?.toLowerCase()));
    }
    // Search across all visible data fields: Title, Severity, Status, Description, Remediation
    if (search) {
      const searchLower = search.toLowerCase();
      rows = rows.filter(check => {
        const title = check.Title?.toLowerCase() || '';
        const severity = (check.Severity || '').toLowerCase();
        const status = (check.Status || '').toLowerCase();
        const description = check.Description?.toLowerCase() || '';
        let remediation = '';
        if (typeof check.Remediation === 'string') {
          remediation = check.Remediation.toLowerCase();
        } else if (typeof check.Remediation === 'object' && check.Remediation?.Text) {
          remediation = check.Remediation.Text.toLowerCase();
        }
        return (
          title.includes(searchLower) ||
          severity.includes(searchLower) ||
          status.includes(searchLower) ||
          description.includes(searchLower) ||
          remediation.includes(searchLower)
        );
      });
    }
    setFilteredRows(rows);
  }, [sortedChecks, selectedStatus, selectedSeverity, selectedService, activeFilters, search]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get visible columns
  const visibleColumns = DEFAULT_COLUMNS;
  // Get paginated rows
  const paginatedRows = useMemo(() => {
    return filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Empty/error state
  if (scanObj === undefined || scanObj === null) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress color="primary" />
        <Typography variant="h6" color="text.secondary" mt={2}>
          Loading scan data...
        </Typography>
      </Box>
    );
  }
  if (scanObj && (!checks || !checks.length)) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No checks found for this scan.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden', mt: 2 }}>
      {/* Filters Bar */}
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Status Filter */}
          <Select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
            renderValue={val => {
              if (val === 'all') return 'Status';
              const { text } = getStatusBadgeProps(val);
              return <Typography sx={{ color: text, fontWeight: 500, fontSize: 14 }}>{val}</Typography>;
            }}
          >
            <MenuItem value="all">Status</MenuItem>
            {availableStatuses.map((status) => {
              const { text } = getStatusBadgeProps(status);
              return (
                <MenuItem key={status} value={status}>
                  <Typography sx={{ color: text, fontWeight: 500, fontSize: 14 }}>{status}</Typography>
                </MenuItem>
              );
            })}
          </Select>
          {/* Severity Filter */}
          <Select
            value={selectedSeverity}
            onChange={(e) => setSelectedSeverity(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
            renderValue={val => {
              if (val === 'all') return 'Severity';
              const { text } = getSeverityBadgeProps(val);
              return <Typography sx={{ color: text, fontWeight: 500, fontSize: 14 }}>{val}</Typography>;
            }}
          >
            <MenuItem value="all">Severity</MenuItem>
            {availableSeverities.map((sev) => {
              const { text } = getSeverityBadgeProps(sev);
              return (
                <MenuItem key={sev} value={sev}>
                  <Typography sx={{ color: text, fontWeight: 500, fontSize: 14 }}>{sev}</Typography>
                </MenuItem>
              );
            })}
          </Select>
          {/* Service Filter */}
          <Select
            value={selectedService}
            onChange={(e) => setSelectedService(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
            renderValue={val => val === 'all' ? 'Service' : val}
          >
            <MenuItem value="all">Service</MenuItem>
            {availableServices.map(service => (
              <MenuItem key={service} value={service}>{service}</MenuItem>
            ))}
          </Select>
        </Box>
        {/* Search - right aligned */}
        <Box sx={{ display: 'flex', alignItems: 'center', background: '#F1F1F1', borderRadius: 1, px: 1, height: 36, ml: 'auto' }}>
          <SearchIcon sx={{ color: '#888', fontSize: 20, mr: 1 }} />
          <InputBase
            placeholder="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: 200, fontSize: 14 }}
          />
        </Box>
      </Box>
      {/* Table */}
      <TableContainer sx={{ borderRadius: 2, boxShadow: '0px 4px 12px rgba(0,0,0,0.08)', flex: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {visibleColumns.map(col => {
                // Only NumResourcesScanned is sortable
                const isSortable = col.key === 'NumResourcesScanned';
                return (
                  <TableCell 
                    key={col.key} 
                    align={col.key === "Title" || col.key === "CheckId" ? "left" : "center"} 
                    sx={{ fontWeight: 700, fontSize: 15, background: '#F5F5F5', color: '#333', cursor: isSortable ? 'pointer' : 'default', userSelect: 'none', position: 'relative' }}
                    onClick={isSortable ? () => handleSort(col.key) : undefined}
                  >
                    {col.label}
                    {/* Only show sort arrow for Resources Scanned (NumResourcesScanned) */}
                    {col.key === 'NumResourcesScanned' && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          ml: 0.5,
                          verticalAlign: 'middle',
                          visibility: sortConfig.key === col.key ? 'visible' : 'hidden',
                          // Show arrow on hover
                          '&:hover, &:focus, &:active, .MuiTableCell-root:hover &': {
                            visibility: 'visible',
                          },
                        }}
                      >
                        {sortConfig.key === col.key ? (
                          sortConfig.direction === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        ) : (
                          // Default arrow (down) when not sorted, only on hover
                          <ArrowDownwardIcon sx={{ fontSize: 16, color: '#bbb' }} />
                        )}
                      </Box>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row, idx) => (
              <React.Fragment key={row.CheckId || idx}>
                <TableRow hover selected={expandedRow === (row.CheckId || idx)}>
                  {visibleColumns.map(col => {
                    if (col.key === "Title") {
                      return (
                        <TableCell key={col.key} align="left" sx={{ minWidth: 220, maxWidth: 600, p: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                            <IconButton
                              size="small"
                              onClick={() => setExpandedRow(expandedRow === (row.Title || idx) ? null : (row.Title || idx))}
                              aria-label={expandedRow === (row.Title || idx) ? 'Collapse row' : 'Expand row'}
                            >
                              {expandedRow === (row.Title || idx) ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                            </IconButton>
                            <Typography
                              variant="body2"
                              fontWeight={400}
                              sx={{
                                whiteSpace: 'normal',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontSize: 14,
                                maxWidth: 520,
                                wordBreak: 'break-word',
                                lineHeight: 1.3
                              }}
                              title={row.Title}
                            >
                              {row.Title}
                            </Typography>
                          </Box>
                        </TableCell>
                      );
                    }
                    if (col.key === "Severity") {
                      // Severity badge color logic
                      const sev = (row.Severity || '').toUpperCase();
                      const { text } = getSeverityBadgeProps(sev);
                      return (
                        <TableCell key={col.key} align="center" sx={{ p: 0.5 }}>
                          <Typography sx={{ color: text, fontWeight: 500, fontSize: 14 }}>{sev}</Typography>
                        </TableCell>
                      );
                    }
                    if (col.key === "Status") {
                      const { text } = getStatusBadgeProps(row.Status);
                      return (
                        <TableCell key={col.key} align="center">
                          <Typography sx={{ color: text, fontWeight: 500, fontSize: 14 }}>{row.Status}</Typography>
                        </TableCell>
                      );
                    }
                    if (col.key === "Type") {
                      return (
                        <TableCell key={col.key} align="center">{row.Type || row.RiskType}</TableCell>
                      );
                    }
                    if (col.key === "Region") {
                      return (
                        <TableCell key={col.key} align="center">{row.Region}</TableCell>
                      );
                    }
                    if (col.key === "NumResourcesScanned") {
                      const totalResources = row.NumResourcesScanned || 0;
                      const failedResources = row.NumResourcesFailed || 0;
                      const passedResources = totalResources - failedResources;
                      const isZero = totalResources === 0;
                      
                      return (
                        <TableCell key={col.key} align="center">
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 0.5, 
                            justifyContent: 'center', 
                            width: '100%',
                            minHeight: 32,
                            alignItems: 'center'
                          }}>
                            {/* Failed Badge */}
                            <Box sx={{ width: '33%', display: 'flex', justifyContent: 'center' }}>
                              <Tooltip title={`${failedResources} failed ${totalResources === 1 ? 'resource' : 'resources'}`}>
                                <span>
                                  <Chip
                                    label={`Failed: ${failedResources}`}
                                    size="small"
                                    onClick={isZero ? undefined : () => {
                                      setSelectedCheckId(row.CheckId);
                                      setSelectedRunId(scanObj?.runId);
                                      setDrawerAvailableRegions(row.AvailableRegions || []);
                                      setDrawerAvailableTypes(row.AvailableTypes || []);
                                      setDrawerCheckTitle(row.Title || "");
                                      setDrawerOpen(true);
                                      // Pass filter state to drawer
                                      setTimeout(() => {
                                        const event = new CustomEvent('setCspmFilter', { 
                                          detail: { filter: 'FAILED' } 
                                        });
                                        window.dispatchEvent(event);
                                      }, 100);
                                    }}
                                    sx={{
                                      backgroundColor: isZero ? "#f1f1f1" : "#ffebee",
                                      color: isZero ? "#aaa" : "#d32f2f",
                                      cursor: isZero ? "not-allowed" : "pointer",
                                      opacity: isZero ? 0.5 : 1,
                                      pointerEvents: isZero ? 'none' : 'auto',
                                      '&:hover': isZero ? {} : { backgroundColor: "#ffcdd2" },
                                      fontSize: '0.75rem',
                                      height: 24,
                                      minWidth: 100,
                                      maxWidth: 120,
                                      whiteSpace: 'nowrap',
                                      overflow: 'visible',
                                    }}
                                    disabled={isZero}
                                  />
                                </span>
                              </Tooltip>
                            </Box>
                            
                            {/* Passed Badge */}
                            <Box sx={{ width: '33%', display: 'flex', justifyContent: 'center' }}>
                              <Tooltip title={`${passedResources} passed ${totalResources === 1 ? 'resource' : 'resources'}`}>
                                <span>
                                  <Chip
                                    label={`Passed: ${passedResources}`}
                                    size="small"
                                    onClick={isZero ? undefined : () => {
                                      setSelectedCheckId(row.CheckId);
                                      setSelectedRunId(scanObj?.runId);
                                      setDrawerAvailableRegions(row.AvailableRegions || []);
                                      setDrawerAvailableTypes(row.AvailableTypes || []);
                                      setDrawerCheckTitle(row.Title || "");
                                      setDrawerOpen(true);
                                      // Pass filter state to drawer
                                      setTimeout(() => {
                                        const event = new CustomEvent('setCspmFilter', { 
                                          detail: { filter: 'PASSED' } 
                                        });
                                        window.dispatchEvent(event);
                                      }, 100);
                                    }}
                                    sx={{
                                      backgroundColor: isZero ? "#f1f1f1" : "#e8f5e9",
                                      color: isZero ? "#aaa" : "#2e7d32",
                                      cursor: isZero ? "not-allowed" : "pointer",
                                      opacity: isZero ? 0.5 : 1,
                                      pointerEvents: isZero ? 'none' : 'auto',
                                      '&:hover': isZero ? {} : { backgroundColor: "#c8e6c9" },
                                      fontSize: '0.75rem',
                                      height: 24,
                                      minWidth: 100,
                                      maxWidth: 120,
                                      whiteSpace: 'nowrap',
                                      overflow: 'visible',
                                    }}
                                    disabled={isZero}
                                  />
                                </span>
                              </Tooltip>
                            </Box>
                            
                            {/* Total Badge */}
                            <Box sx={{ width: '33%', display: 'flex', justifyContent: 'center' }}>
                              <Tooltip title={`${totalResources} total ${totalResources === 1 ? 'resource' : 'resources'}`}>
                                <span>
                                  <Chip
                                    label={`Total: ${totalResources}`}
                                    size="small"
                                    onClick={isZero ? undefined : () => {
                                      setSelectedCheckId(row.CheckId);
                                      setSelectedRunId(scanObj?.runId);
                                      setDrawerAvailableRegions(row.AvailableRegions || []);
                                      setDrawerAvailableTypes(row.AvailableTypes || []);
                                      setDrawerCheckTitle(row.Title || "");
                                      setDrawerOpen(true);
                                      // Pass filter state to drawer (no filter for total)
                                      setTimeout(() => {
                                        const event = new CustomEvent('setCspmFilter', { 
                                          detail: { filter: null } 
                                        });
                                        window.dispatchEvent(event);
                                      }, 100);
                                    }}
                                    sx={{
                                      backgroundColor: isZero ? "#f1f1f1" : "#e3f2fd",
                                      color: isZero ? "#aaa" : "#1976d2",
                                      cursor: isZero ? "not-allowed" : "pointer",
                                      opacity: isZero ? 0.5 : 1,
                                      pointerEvents: isZero ? 'none' : 'auto',
                                      '&:hover': isZero ? {} : { backgroundColor: "#bbdefb" },
                                      fontSize: '0.75rem',
                                      height: 24,
                                      minWidth: 100,
                                      maxWidth: 120,
                                      whiteSpace: 'nowrap',
                                      overflow: 'visible',
                                    }}
                                    disabled={isZero}
                                  />
                                </span>
                              </Tooltip>
                            </Box>
                          </Box>
                        </TableCell>
                      );
                    }
                    return null;
                  })}
                </TableRow>
                {/* Expandable detail row */}
                {expandedRow === (row.Title || idx) && (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} sx={{ background: '#f8fafd', borderBottom: '1px solid #e3eafc', p: 2 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, color: '#1a2233' }}>
                          Description:
                          <Typography component="span" variant="body2" sx={{ ml: 1, color: '#222' }}>
                            {row.Description}
                          </Typography>
                        </Typography>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, color: '#1a2233' }}>
                          Remediation:
                          <Typography component="span" variant="body2" sx={{ ml: 1, color: '#222' }}>
                            {typeof row.Remediation === 'object' ? row.Remediation.Text : row.Remediation}
                          </Typography>
                          {row.Remediation?.Url && (
                            <a href={row.Remediation.Url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8, color: '#1976d2', textDecoration: 'underline' }}>Reference</a>
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination */}
      <TablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          borderTop: '1px solid #e0e0e0',
          '.MuiTablePagination-toolbar': {
            height: '52px',
          },
        }}
      />
      <CSPMResourcesDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        checkId={selectedCheckId}
        runId={selectedRunId}
        availableRegions={drawerAvailableRegions}
        availableTypes={drawerAvailableTypes}
        checkTitle={drawerCheckTitle}
      />
    </Paper>
  );
};

export default CSPMGrid; 